import {
  ValidationType,
  NumberFormatType,
  BaseValidationPattern,
  NumberFormatConfig,
} from "../types/validation";

// Validation patterns for various field types
// Each pattern includes a regex, error message, and length constraints
export const ValidationPatterns: Record<
  ValidationType,
  BaseValidationPattern
> & {
  numberFormats: Record<NumberFormatType, NumberFormatConfig>;
} = {
  none: {
    defaultMaxLength: 255,
    textareaMaxLength: 2000,
    errorMessage: "",
  },

  email: {
    pattern:
      "^[a-zA-Z0-9](?:[a-zA-Z0-9._%+-]*[a-zA-Z0-9])?@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)*\\.[a-zA-Z]{2,}$",
    errorMessage:
      "Please enter a valid email address (e.g., john.doe@company.co.za)",
    maxLength: 254,
    defaultMaxLength: 254,
  },

  phone: {
    pattern: "^(\\+27|0)[6-8][0-9]{8}$",
    errorMessage:
      "Please enter a valid South African phone number (e.g., 0712345678 or +27712345678)",
    minLength: 10,
    maxLength: 12,
    defaultMaxLength: 12,
  },

  url: {
    pattern:
      "^https?:\\/\\/(?:www\\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b(?:[-a-zA-Z0-9()@:%_+.~#?&//=]*)$",
    errorMessage: "Please enter a valid URL (e.g., https://example.com)",
    maxLength: 2048,
    defaultMaxLength: 2048,
  },

  zipcode: {
    pattern: "^[0-9]{4}$",
    errorMessage: "Please enter a valid South African postal code (4 digits)",
    minLength: 4,
    maxLength: 4,
    defaultMaxLength: 4,
  },

  username: {
    pattern: "^[a-zA-Z0-9_-]{3,32}$",
    errorMessage:
      "Username must be 3-32 characters and can contain letters, numbers, underscores, and hyphens",
    minLength: 3,
    maxLength: 32,
    defaultMaxLength: 32,
  },

  password: {
    pattern:
      "^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@$!%*?&])[A-Za-z\\d@$!%*?&]{8,}$",
    errorMessage:
      "Password must be at least 8 characters and include uppercase, lowercase, number, and special character",
    minLength: 8,
    maxLength: 128,
    defaultMaxLength: 128,
  },

  name: {
    pattern: "^[a-zA-Z][a-zA-Z .'-]*[a-zA-Z.]$",
    errorMessage:
      "Please enter a valid name (letters, spaces, and basic punctuation only)",
    maxLength: 70,
    defaultMaxLength: 70,
  },

  alphanumeric: {
    pattern: "^[a-zA-Z0-9]+$",
    errorMessage: "Only letters and numbers allowed",
    maxLength: 255,
    defaultMaxLength: 255,
  },

  numberFormats: {
    decimal: {
      pattern: "^-?\\d+(\\.\\d+)?$",
      errorMessage: "Please enter a valid decimal number (e.g., 123.45)",
      defaultMaxLength: 20,
      allowNegative: true,
      precision: 8,
    },
    integer: {
      pattern: "^-?\\d+$",
      errorMessage: "Please enter a whole number without decimals (e.g., 123)",
      defaultMaxLength: 12,
      allowNegative: true,
    },
    currency: {
      pattern: "^\\$?\\d+(\\.\\d{2})?$",
      errorMessage: "Please enter a valid currency amount (e.g., $123.45)",
      defaultMaxLength: 15,
      allowNegative: false,
      precision: 2,
      prefixSymbol: "$",
    },
    percentage: {
      pattern: "^\\d+(\\.\\d+)?%?$",
      errorMessage: "Please enter a valid percentage (e.g., 75 or 75.5)",
      defaultMaxLength: 6,
      allowNegative: false,
      precision: 2,
      suffixSymbol: "%",
      max: 100,
    },
  },
} as const;